import { getKeyboardFocusableElements, mql, onLoadAndResize } from "../utils";
import store from "../store/index";

import { Tabs } from "./tabs";
import { L2Tabs } from "./l2-tabs";
import { Dialog } from "./dialog";
import { DialogTabs } from "./dialog-tabs";
import { FocusTrap } from "./focus-trap";

const SELECTORS = {
  mainMenuDialogDesktop: ".js-main-menu-dialog-desktop",
  mainMenuDialogMobile: ".js-main-menu-dialog-mobile",
  mainMenuSubmenu: ".js-main-menu-submenu",
  mainMenuTab: ".js-main-menu-tab",
  mainMenuMobile: ".js-main-menu-mobile",
  menuBtnMobile: ".js-menu-btn-mobile",
  searchBtnMobile: ".js-search-btn-dialog",
  searchPanelMobile: ".js-search-panel-mobile",
};

const CLASSES = {
  menuActive: "menu-active",
  hidden: "hidden",
  tabActive: "tab-active",
};

class MainMenu {
  constructor(context) {
    this.context = context;
    this.mobileDialogContext = null;
    this.desktopDialogContext = null;
    this.mobileTabs = null;
    this.mobileTabpanelFocusTrap = null;
    this.desktopDialogTabs = null;
    this.desktopSubmenus = null;
    this.desktopImages = null;
    this.mql = null;
  }

  init() {
    this.mobileDialogContext = this.context.querySelector(
      SELECTORS.mainMenuDialogMobile,
    );
    this.desktopDialogContext = this.context.querySelector(
      SELECTORS.mainMenuDialogDesktop,
    );
    this.mainMenuMobile = this.context.querySelector(SELECTORS.mainMenuMobile);
    this.menuBtnMobile = this.context.querySelector(SELECTORS.menuBtnMobile);
    this.searchPanelMobile = this.context.querySelector(
      SELECTORS.searchPanelMobile,
    );
    this.searchBtnMobile = this.context.querySelector(
      SELECTORS.searchBtnMobile,
    );

    this.mobileTabs = new Tabs(this.mobileDialogContext, SELECTORS.mainMenuTab);
    this.mobileDialog = new Dialog(this.mobileDialogContext);
    this.desktopDialogTabs = new DialogTabs(this.desktopDialogContext);
    if (this.desktopDialogContext !== null) {
      this.desktopSubmenus = [
        ...this.desktopDialogContext.querySelectorAll(
          SELECTORS.mainMenuSubmenu,
        ),
      ].map((submenu) => ({
        tabs: new L2Tabs(submenu, SELECTORS.mainMenuTab),
      }));
    }

    this.mobileTabs.init();
    this.mobileDialog.init();
    this.mobileTabpanelFocusTrap = this.mobileTabs.tabpanels.reduce(
      (acc, tabpanel) => {
        acc[tabpanel.dataset.tab] = new FocusTrap(tabpanel).prependContext(
          this.mobileDialog.dialogHeader,
        );
        return acc;
      },
      {},
    );
    this.desktopDialogTabs.init();
    this.desktopSubmenus.forEach((submenu) => {
      submenu.tabs.init();
    });

    this.mql = mql("large");

    this.subscribe();
  }

  subscribe() {
    this.mobileTabs.onToggle((instance, element) => {
      const tab = this.mobileTabs.getAssociatedTab(element.dataset.tab);
      const tabpanel = this.mobileTabs.getAssociatedTabpanel(
        element.dataset.tab,
      );
      const firstFocusableTabpanelElement =
        getKeyboardFocusableElements(tabpanel)[0];

      this.activateMenu("mobile");

      // Init tabpanel focus trap
      this.mobileTabpanelFocusTrap[element.dataset.tab].init({
        focusFirstElement: firstFocusableTabpanelElement,
        focusBackElement: tab,
      });
    });

    this.mobileTabs.onClose((instance, element) => {
      this.deactivateMenu("mobile");

      // Suspend tabpanel focus trap
      this.mobileTabpanelFocusTrap[element.dataset.tab].suspend();
    });

    this.desktopDialogTabs.dialog.onClose(() => {
      this.resetDesktopSubmenus();
    });

    this.desktopDialogTabs.tabs.onToggle(() => {
      this.deactivateMenu("desktop");
      this.resetDesktopSubmenus();
    });

    this.desktopSubmenus.forEach((submenu) => {
      submenu.tabs.onToggle(() => {
        this.activateMenu("desktop");
      });
    });

    this.mql.addListener((e) => {
      if (e.matches) {
        this.mobileDialog.hideDialog();
      } else {
        this.desktopDialogTabs.dialog.hideDialog();
      }
    });

    window.addEventListener("load", () => {
      onLoadAndResize(() => {
        if (this.desktopImages === null && this.mql.matches) {
          // unveil desktop dialog main menu images as soon as possible to avoid lazy loading flash ins
          this.desktopImages = [
            ...this.desktopDialogTabs.dialog.dialog.querySelectorAll("img"),
          ].map((image) => {
            window.lazySizes.loader.unveil(image);
            return image;
          });
        }
      }).apply();
    });

    this.mobileDialog.onClose(() => {
      this.resetSearchPanel();
      store.dispatch("mainMenuMobileOpen", { mainMenuMobileOpen: false });
      store.dispatch("searchPanelMobileOpen", { searchPanelMobileOpen: false });
    });

    if (this.menuBtnMobile) {
      this.menuBtnMobile.addEventListener("click", () => {
        const shouldCloseMainMenu = store.state.mainMenuMobileOpen;

        this.toggleMobilePanel("main-menu", shouldCloseMainMenu);
      });
    }

    if (this.searchBtnMobile) {
      this.searchBtnMobile.addEventListener("click", () => {
        const shouldCloseSearchPanel = store.state.searchPanelMobileOpen;

        this.toggleMobilePanel("search-panel", shouldCloseSearchPanel);
      });
    }
  }

  resetDesktopSubmenus() {
    this.desktopSubmenus.forEach((submenu) => {
      submenu.tabs.makeAllTabsInactive().resetPanel();
    });
  }

  toggleMobilePanel(panel, shouldClose) {
    if (shouldClose) {
      this.mobileDialog.hideDialog();
    } else {
      const isMainMenu = panel === "main-menu";

      if (this.searchBtnMobile) {
        this.searchBtnMobile.classList.toggle(CLASSES.tabActive, !isMainMenu);
      }

      this.menuBtnMobile.classList.toggle(CLASSES.tabActive, isMainMenu);
      if (this.searchPanelMobile) {
        this.searchPanelMobile.classList.toggle(CLASSES.hidden, isMainMenu);
      }
      this.mainMenuMobile.classList.toggle(CLASSES.hidden, !isMainMenu);
      store.dispatch("mainMenuMobileOpen", { mainMenuMobileOpen: isMainMenu });
      store.dispatch("searchPanelMobileOpen", {
        searchPanelMobileOpen: !isMainMenu,
      });
    }
  }

  activateMenu(type) {
    this.context.classList.add(CLASSES.menuActive);

    switch (type) {
      case "mobile":
        this.mobileDialog.dialog.classList.add(CLASSES.menuActive);
        break;
      case "desktop":
        this.desktopDialogTabs.dialog.dialog.classList.add(CLASSES.menuActive);
        break;
    }

    return this;
  }

  deactivateMenu(type) {
    this.context.classList.remove(CLASSES.menuActive);

    switch (type) {
      case "mobile":
        this.mobileDialog.dialog.classList.remove(CLASSES.menuActive);
        break;
      case "desktop":
        this.desktopDialogTabs.dialog.dialog.classList.remove(
          CLASSES.menuActive,
        );
        break;
    }

    return this;
  }

  resetSearchPanel() {
    if (this.searchBtnMobile) {
      this.searchBtnMobile.classList.remove(CLASSES.tabActive);
    }
    if (this.menuBtnMobile) {
      this.menuBtnMobile.classList.add(CLASSES.tabActive);
    }
    if (this.searchPanelMobile) {
      this.searchPanelMobile.classList.add(CLASSES.hidden);
    }
    if (this.mainMenuMobile) {
      this.mainMenuMobile.classList.remove(CLASSES.hidden);
    }
  }
}

export { MainMenu };
